import { z } from 'zod';
import { DISASSEMBLY_LEVELS, FINISH_KEYS, MODIFIER_KEYS, SIZE_CLASSES, } from '../constants.js';
import { moneyCents, uuidSchema } from './common.js';
// Integer-unit primitives (§29: cents / minutes / bp / ft_x10 are all ints).
const intNonNeg = z.number().int().min(0);
const bp = z.number().int(); // basis points may be signed for adjustments
const bpNonNeg = z.number().int().min(0);
const serviceKindSchema = z.enum(['wrap', 'ppf', 'tint', 'ceramic', 'print']);
// ---------------------------------------------------------------------------
// §29.6 — vehicle catalog search.
// ---------------------------------------------------------------------------
export const vehicleCatalogQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    year: z.coerce.number().int().min(1950).max(2100).optional(),
    make: z.string().trim().max(60).optional(),
    model: z.string().trim().max(80).optional(),
    sizeClass: z.enum(SIZE_CLASSES).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const vehicleCatalogTrimSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    yearStart: z.number().int(),
    yearEnd: z.number().int().nullable(),
    complexityBp: bpNonNeg,
    baseFtX10: intNonNeg,
    baseMinutes: intNonNeg,
    modifiers: z.array(z.enum(MODIFIER_KEYS)),
});
export const vehicleCatalogItemSchema = z.object({
    id: uuidSchema,
    make: z.string(),
    model: z.string(),
    sizeClass: z.enum(SIZE_CLASSES),
    baseFtX10: intNonNeg,
    baseMinutes: intNonNeg,
    complexityBp: bpNonNeg,
    trims: z.array(vehicleCatalogTrimSchema),
    // §34.1/§35 — platform defaults for this catalog vehicle (vehicle_catalog.
    // default_l1/l2/l3_disassembly_minutes, migrations 0039/0048). This endpoint
    // has no workspace scope, so it can only surface the platform default, not
    // any workspace override - the workspace-resolved "effective" minutes are
    // exposed by the vehicles-panel endpoint instead.
    catalogDefaultL1Minutes: intNonNeg,
    catalogDefaultL2Minutes: intNonNeg,
    catalogDefaultL3Minutes: intNonNeg,
});
export const vehicleCatalogResponseSchema = z.object({
    items: z.array(vehicleCatalogItemSchema),
});
// ---------------------------------------------------------------------------
// §29.6 — workspace vehicle create / update.
// ---------------------------------------------------------------------------
// §34.1/§35 — L1/L2/L3 disassembly minutes, capped to the same bound as the
// vehicle_disassembly_overrides / workspace_vehicles check constraints (0039/0048).
const disassemblyMinutesField = z.number().int().min(0).max(6000);
export const workspaceVehicleInputSchema = z.object({
    catalogVehicleId: uuidSchema.nullable().optional(),
    catalogTrimId: uuidSchema.nullable().optional(),
    label: z.string().trim().min(1).max(120),
    sizeClass: z.enum(SIZE_CLASSES),
    baseFtX10: intNonNeg,
    baseMinutes: intNonNeg,
    complexityBp: bpNonNeg,
    isCustom: z.boolean().default(false),
    // §34.1/§35 — required from now on (workspace_vehicles.*_disassembly_minutes
    // stay nullable in the DB only for legacy rows created before this field
    // existed; every new create must supply all three levels).
    l1DisassemblyMinutes: disassemblyMinutesField,
    l2DisassemblyMinutes: disassemblyMinutesField,
    l3DisassemblyMinutes: disassemblyMinutesField,
});
export const workspaceVehicleUpdateSchema = workspaceVehicleInputSchema.partial();
// §29.6 — list this workspace's own custom vehicles (created via "Vehicle not
// listed?"), so a later quote can search and reuse one instead of only ever
// seeing the platform vehicle_catalog.
export const workspaceVehicleListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
});
export const workspaceVehicleListItemSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    sizeClass: z.enum(SIZE_CLASSES),
    baseFtX10: intNonNeg,
    baseMinutes: intNonNeg,
    complexityBp: bpNonNeg,
    l1DisassemblyMinutes: intNonNeg,
    l2DisassemblyMinutes: intNonNeg,
    l3DisassemblyMinutes: intNonNeg,
});
export const workspaceVehicleListResponseSchema = z.object({ items: z.array(workspaceVehicleListItemSchema) });
// Single catalog trim, resolved to this workspace's effective L1/L2/L3
// disassembly minutes (catalog default, or this workspace's override if one
// exists) - lets a quote line editor fully reconstruct the exact VehicleSelection
// a saved line picked (VehiclePicker.tsx's pickTrim, client-side), not just its
// display label.
export const vehicleCatalogTrimDetailSchema = z.object({
    catalogVehicleId: uuidSchema,
    catalogTrimId: uuidSchema,
    make: z.string(),
    model: z.string(),
    trimName: z.string(),
    sizeClass: z.enum(SIZE_CLASSES),
    baseFtX10: intNonNeg,
    baseMinutes: intNonNeg,
    complexityBp: bpNonNeg,
    modifiers: z.array(z.enum(MODIFIER_KEYS)),
    l1DisassemblyMinutes: intNonNeg,
    l2DisassemblyMinutes: intNonNeg,
    l3DisassemblyMinutes: intNonNeg,
});
// ---------------------------------------------------------------------------
// §34.1 — vehicle disassembly overrides (PUT upsert / DELETE reset) + the
// merged Settings Vehicles panel (GET, server-paginated).
// ---------------------------------------------------------------------------
export const vehicleDisassemblyOverridePutSchema = z.object({
    l1Minutes: disassemblyMinutesField,
    l2Minutes: disassemblyMinutesField,
    l3Minutes: disassemblyMinutesField,
});
const VEHICLES_PANEL_PAGE_SIZES = [25, 50, 100];
export const vehiclesPanelQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().refine((v) => VEHICLES_PANEL_PAGE_SIZES.includes(v), {
        message: 'pageSize must be one of 25, 50, 100',
    }).default(25),
    q: z.string().trim().max(80).optional(),
    sizeClass: z.enum(SIZE_CLASSES).optional(),
});
export const vehiclesPanelRowSchema = z.object({
    id: uuidSchema,
    source: z.enum(['catalog', 'custom', 'overridden']),
    make: z.string(),
    model: z.string(),
    trimName: z.string().nullable(),
    sizeClass: z.enum(SIZE_CLASSES),
    l1Minutes: intNonNeg,
    l2Minutes: intNonNeg,
    l3Minutes: intNonNeg,
    catalogDefaultL1: intNonNeg.nullable(),
    catalogDefaultL2: intNonNeg.nullable(),
    catalogDefaultL3: intNonNeg.nullable(),
});
export const vehiclesPanelResponseSchema = z.object({
    total: intNonNeg,
    customCount: intNonNeg,
    rows: z.array(vehiclesPanelRowSchema),
});
// ---------------------------------------------------------------------------
// §29.6 — pricing profile (ResolvedProfileVersion as a zod schema).
//
// Split into the shop-wide rate fields (shop_rates_versions, one per
// workspace) and the per-service fields (pricing_profile_versions, one per
// workspace per service_kind). resolvedProfileVersionSchema is redefined as
// the merge of the two so its inferred type and runtime validation behavior
// are unchanged for every existing consumer.
// ---------------------------------------------------------------------------
export const resolvedShopRatesSchema = z.object({
    laborRateCentsPerHour: intNonNeg,
    removalRateCentsPerHour: intNonNeg.nullable(),
    designRateCentsPerHour: intNonNeg,
    wasteBp: bpNonNeg,
    markupBp: bpNonNeg,
    minJobPriceCents: intNonNeg,
});
export const resolvedServiceProfileSchema = z.object({
    revisionRoundsIncluded: intNonNeg,
    rushBp: bpNonNeg,
    mobileInstallFeeCents: intNonNeg,
    fleetDiscountThreshold: intNonNeg,
    fleetDiscountBp: bpNonNeg,
    exoticLiabilityBp: bpNonNeg,
    costPerFtCents: intNonNeg,
    disassemblyL1Minutes: intNonNeg,
    disassemblyL2Minutes: intNonNeg,
    disassemblyL3Minutes: intNonNeg,
    doorJambsMinutes: intNonNeg,
    doorJambsFtX10: intNonNeg,
});
export const resolvedProfileVersionSchema = resolvedShopRatesSchema.merge(resolvedServiceProfileSchema);
export const pricingProfileResponseSchema = z.object({
    serviceKind: serviceKindSchema,
    versionId: uuidSchema,
    version: z.number().int(),
    resolved: resolvedProfileVersionSchema,
    updatedAt: z.string(),
});
/** PUT body - creates a new append-only pricing_profile_versions row. */
export const pricingProfilePutSchema = resolvedProfileVersionSchema;
// ---------------------------------------------------------------------------
// Shop rates (shop_rates / shop_rates_versions) — workspace-global, no
// serviceKind discriminator since these rates are shared across all 4
// service kinds. Mirrors pricingProfileResponseSchema/pricingProfilePutSchema
// scoped to just the 6 shop-rate fields.
// ---------------------------------------------------------------------------
export const shopRatesResponseSchema = z.object({
    versionId: uuidSchema,
    version: z.number().int(),
    resolved: resolvedShopRatesSchema,
    updatedAt: z.string(),
});
/** PUT body - creates a new append-only shop_rates_versions row. */
export const shopRatesPutSchema = resolvedShopRatesSchema;
// ---------------------------------------------------------------------------
// New pricing-tab config rows (mutable, not append-only): PPF material
// tiers / zone price overrides, tint price matrix, ceramic package tiers.
// ---------------------------------------------------------------------------
export const sizeBucketSchema = z.enum(['coupe_sedan', 'suv_truck', 'van_oversize']);
export const ppfMaterialTierSchema = z.object({
    filmTier: z.enum(['standard', 'premium_self_healing', 'stealth_matte', 'colored_ppf']),
    costPerSqftCents: intNonNeg,
});
export const ppfZonePriceOverrideSchema = z.object({
    zoneKey: z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
    sizeClass: z.enum(SIZE_CLASSES),
    priceCents: intNonNeg.nullable(),
});
// Platform-wide, read-only (ppf_zone_defs — migration 0012/0014, seeded once,
// no workspace_id, never mutated by this feature).
export const ppfZoneDefSchema = z.object({
    zoneKey: z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
    sizeClass: z.enum(SIZE_CLASSES),
    sqftX10: intNonNeg,
    minutes: intNonNeg,
    cureHoldMinutes: intNonNeg,
});
/** PUT body for a single ppf_material_tiers row - all 4 tiers already exist
 * per-workspace (0023 seed), so this is a per-key update, mirroring
 * updateSizeMultiplierSchema's PATCH-one-row idiom (catalog.ts). */
export const ppfMaterialTierPutSchema = z.object({ costPerSqftCents: intNonNeg });
/** PUT body for a single ppf_zone_price_overrides row - a real upsert (rows
 * don't pre-exist), null clears the override back to "no override". */
export const ppfZonePriceOverridePutSchema = z.object({ priceCents: intNonNeg.nullable() });
// ---------------------------------------------------------------------------
// Wrap partial-panel pricing (panel_defs platform defaults + per-workspace
// workspace_panel_overrides) - resolve.ts's resolvePartialPanels() reads the
// same two tables via override-then-default lookup; this is the settings-UI
// mirror of that data so a shop can see/edit its own panel footage/labor.
// ---------------------------------------------------------------------------
export const PANEL_KEYS = [
    'hood', 'roof', 'trunk_tailgate', 'front_bumper', 'rear_bumper', 'fenders',
    'doors', 'rockers', 'quarter_panels', 'mirrors', 'a_pillars', 'grille_surround', 'badges_delete',
];
/** One cell of the effective panel-pricing matrix (panel_key x size_class) -
 * override merged with default, plus a flag for whether an override exists. */
export const panelPricingCellSchema = z.object({
    panelKey: z.enum(PANEL_KEYS),
    sizeClass: z.enum(SIZE_CLASSES),
    ftX10: intNonNeg,
    minutes: intNonNeg,
    isOverridden: z.boolean(),
});
/** PUT body for a single workspace_panel_overrides row - a real upsert (rows
 * don't pre-exist), mirrors vehicleDisassemblyOverridePutSchema's shape. */
export const panelOverridePutSchema = z.object({
    ftX10: intNonNeg,
    minutes: intNonNeg,
});
export const tintPriceMatrixCellSchema = z.object({
    zoneKey: z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
    filmGrade: z.enum(['dyed', 'carbon', 'ceramic']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
    removalIncluded: z.boolean(),
});
export const ceramicPackageTierSchema = z.object({
    packageKey: z.enum(['silver', 'gold', 'platinum']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
});
/** PUT body for a single tint_price_matrix row - all 45 cells already exist
 * per-workspace (0024 seed), so this is a per-key update. */
export const tintPriceMatrixCellPutSchema = z.object({ priceCents: intNonNeg, removalIncluded: z.boolean() });
/** PUT body for a single ceramic_package_tiers row - all 9 rows already exist
 * per-workspace (0025 seed), so this is a per-key update. */
export const ceramicPackageTierPutSchema = z.object({ priceCents: intNonNeg });
// Platform-wide, read-only (tint_zone_defs — migration 0012, seeded once,
// no workspace_id, never mutated by this feature).
export const tintZoneDefSchema = z.object({
    zoneKey: z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
    sizeClass: z.enum(SIZE_CLASSES),
    minutes: intNonNeg,
});
// Platform-wide, read-only (ceramic_package_defs — migration 0012, seeded
// once, no workspace_id, never mutated by this feature).
export const ceramicPackageDefSchema = z.object({
    packageKey: z.enum(['silver', 'gold', 'platinum']),
    sizeClass: z.enum(SIZE_CLASSES),
    prepDepth: z.string(),
    layers: intNonNeg,
    warrantyYears: intNonNeg,
    minutes: intNonNeg,
    cureHoldMinutes: intNonNeg,
});
// ---------------------------------------------------------------------------
// §32 Phase 3b - legacy pricing archive (read-only surface over what
// migrate-legacy-pricing.ts wrote in Phase 3a).
// ---------------------------------------------------------------------------
export const legacyPricingArchiveItemSchema = z.object({
    id: uuidSchema,
    sourceTable: z.enum(['service_types', 'coverage_options', 'materials']),
    sourceId: uuidSchema,
    snapshot: z.record(z.string(), z.unknown()),
    reason: z.string(),
    matchedTarget: z.enum(['ppf_zone_price_overrides', 'ppf_material_tiers', 'ceramic_package_tiers']).nullable(),
    createdAt: z.string(),
});
export const legacyPricingArchiveResponseSchema = z.object({
    runId: uuidSchema.nullable(),
    dismissedAt: z.string().nullable(),
    items: z.array(legacyPricingArchiveItemSchema),
});
// ---------------------------------------------------------------------------
// §29.6 — price-preview request/response (mirrors pricing/types.ts shapes).
// ---------------------------------------------------------------------------
const modifierDefSchema = z.object({ addedFtX10: intNonNeg, addedMinutes: intNonNeg });
const finishDefSchema = z.object({ materialBp: bpNonNeg, laborBp: bpNonNeg, extraWasteBp: bpNonNeg });
const wrapCoverageSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('full_wrap') }),
    z.object({
        kind: z.literal('partial_panels'),
        panels: z
            .array(z.object({ panelKey: z.string(), ftX10: intNonNeg, minutes: intNonNeg }))
            .min(1),
    }),
    z.object({ kind: z.literal('chrome_delete'), ftX10: intNonNeg, minutes: intNonNeg }),
    z.object({ kind: z.literal('decals_lettering'), ftX10: intNonNeg, minutes: intNonNeg }),
    z.object({ kind: z.literal('stripes'), ftX10: intNonNeg, minutes: intNonNeg }),
]);
export const wrapPricingInputSchema = z.object({
    laborRateCentsPerHour: intNonNeg,
    designRateCentsPerHour: intNonNeg,
    wasteBp: bpNonNeg,
    markupBp: bpNonNeg,
    costPerFtCents: intNonNeg,
    vehicleBaseFtX10: intNonNeg,
    vehicleBaseMinutes: intNonNeg,
    complexityBp: bpNonNeg,
    coverage: wrapCoverageSchema,
    modifiers: z.array(z.enum(MODIFIER_KEYS)),
    modifierDefs: z.record(z.enum(MODIFIER_KEYS), modifierDefSchema),
    finishKey: z.enum(FINISH_KEYS),
    finishDefs: z.record(z.enum(FINISH_KEYS), finishDefSchema),
    disassemblyLevel: z.enum(DISASSEMBLY_LEVELS),
    disassemblyMinutesByLevel: z.record(z.enum(DISASSEMBLY_LEVELS), intNonNeg),
    doorJambs: z.boolean(),
    jambsFtX10: intNonNeg,
    jambsMinutes: intNonNeg,
    defectPanelCount: intNonNeg,
    defectMinutesPerPanel: intNonNeg,
    correctionFeeCents: moneyCents,
    removalTier: z.enum(['none', 'light', 'moderate', 'heavy_baked']),
    removalMinutesByTier: z.record(z.enum(['light', 'moderate', 'heavy_baked']), intNonNeg),
    designMinutes: intNonNeg,
    printCostCents: moneyCents,
    fixedAddOnCents: moneyCents,
});
export const ppfPricingInputSchema = z.object({
    zoneSqftX10: intNonNeg,
    zoneMinutes: intNonNeg,
    cureHoldMinutes: intNonNeg,
    costPerSqftCents: intNonNeg,
    designMinutes: intNonNeg,
    fixedAddOnCents: moneyCents,
});
export const tintPricingInputSchema = z.object({
    zoneMinutes: intNonNeg,
    priceCents: intNonNeg,
    vlt: z.number().int().min(0).max(100),
    designMinutes: intNonNeg,
    fixedAddOnCents: moneyCents,
});
export const ceramicPricingInputSchema = z.object({
    packageMinutes: intNonNeg,
    cureHoldMinutes: intNonNeg,
    layers: intNonNeg,
    warrantyYears: intNonNeg,
    priceCents: intNonNeg,
    designMinutes: intNonNeg,
    fixedAddOnCents: moneyCents,
});
/** Discriminated price-preview request across service kinds. `profileVersionId`
 * tells the route which `pricing_profile_versions` row to resolve into the
 * `ResolvedProfileVersion` the pure engine needs alongside `input` — the
 * engine itself never fetches anything, so this field exists purely for the
 * server route handler, not the pure engine call. */
export const pricePreviewRequestSchema = z.discriminatedUnion('serviceKind', [
    z.object({ serviceKind: z.literal('wrap'), input: wrapPricingInputSchema, profileVersionId: uuidSchema }),
    z.object({ serviceKind: z.literal('ppf'), input: ppfPricingInputSchema, profileVersionId: uuidSchema }),
    z.object({ serviceKind: z.literal('tint'), input: tintPricingInputSchema, profileVersionId: uuidSchema }),
    z.object({ serviceKind: z.literal('ceramic'), input: ceramicPricingInputSchema, profileVersionId: uuidSchema }),
]);
// PricingResult-shaped response.
const laborItemSchema = z.object({
    label: z.string(),
    minutes: intNonNeg.optional(),
    cents: z.number().int(),
});
export const pricingBreakdownSchema = z.object({
    entries: z.array(laborItemSchema),
    cureHoldMinutes: intNonNeg.optional(),
});
export const pricePreviewResponseSchema = z.object({
    labor: z.array(laborItemSchema),
    estimatedMinutes: intNonNeg,
    filmFtX10: intNonNeg,
    materialCostCents: moneyCents,
    laborCostCents: moneyCents,
    designCostCents: moneyCents,
    printCostCents: moneyCents,
    lineSubtotalCents: moneyCents,
    breakdown: pricingBreakdownSchema,
    cureHoldMinutes: intNonNeg.optional(),
});
// Keep the signed-bp helper referenced so it is part of the public surface for
// quote-level adjustments (fleet discount is a negative bp adjustment).
export const quoteLevelAdjustmentBpSchema = bp;
// ---------------------------------------------------------------------------
// §29.4 rule 8 — line coverage SELECTION SNAPSHOT.
//
// This is the canonical, ID/key-only record of everything the server needs to
// independently RE-DERIVE the pure engine input (never raw prices/minutes the
// client could fabricate). It is stored in `quote_line_items.coverage` (jsonb),
// and is deliberately distinct from `quote_line_items.breakdown` (the fully-
// RESOLVED output snapshot kept for display/immutability). On save the server
// resolves this snapshot against override-then-default DB lookups, recomputes
// with priceServiceLine(), and rejects the line if the client's submitted
// totals don't match (client math is preview-only).
//
// `vehicleCatalogTrimId` / `workspaceVehicleId` / `disassemblyLevel` /
// `doorJambs` / `interiorTrimPieces` are NOT duplicated here — they live in
// their own dedicated quote_line_items columns (0012 ALTER).
// ---------------------------------------------------------------------------
export const lineCoverageSnapshotSchema = z.discriminatedUnion('serviceKind', [
    z.object({
        serviceKind: z.literal('wrap'),
        coverage: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('full_wrap') }),
            z.object({ kind: z.literal('partial_panels'), panelKeys: z.array(z.string()).min(1) }),
            z.object({ kind: z.literal('chrome_delete'), ftX10: intNonNeg, minutes: intNonNeg }),
            z.object({ kind: z.literal('decals_lettering'), ftX10: intNonNeg, minutes: intNonNeg }),
            z.object({ kind: z.literal('stripes'), ftX10: intNonNeg, minutes: intNonNeg }),
        ]),
        // FULL final modifier list already applied (trim's baked-in modifier_keys ∪
        // any quote-time extras) — the resolver does NOT re-fetch the trim's
        // modifier_keys, this is the complete final set.
        modifiers: z.array(z.enum(MODIFIER_KEYS)),
        finishKey: z.enum(FINISH_KEYS),
        removalTier: z.enum(['none', 'light', 'moderate', 'heavy_baked']),
        defectPanelCount: intNonNeg,
        correctionFeeCents: moneyCents,
        designMinutes: intNonNeg,
        printCostCents: moneyCents,
        fixedAddOnCents: moneyCents,
    }),
    z.object({
        serviceKind: z.literal('ppf'),
        zoneKey: z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
        sizeClass: z.enum(SIZE_CLASSES),
        // §36: legacy film-tier path and the shop-product path are mutually
        // exclusive — exactly one of filmTier / ppfProductId is set, same
        // convention as the tint variant's filmGrade / tintProductId below.
        filmTier: z.enum(['standard', 'premium_self_healing', 'stealth_matte', 'colored_ppf']).nullish(),
        ppfProductId: uuidSchema.nullish(),
        designMinutes: intNonNeg,
        fixedAddOnCents: moneyCents,
    }),
    z.object({
        serviceKind: z.literal('tint'),
        zoneKey: z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
        sizeClass: z.enum(SIZE_CLASSES),
        // §36: legacy film-grade path and the shop-product path are mutually
        // exclusive — exactly one of filmGrade / tintProductId is set. The resolver
        // dispatches on which; minutes always come from tint_zone_defs (zone×size),
        // so the product path reproduces legacy minutes exactly.
        filmGrade: z.enum(['dyed', 'carbon', 'ceramic']).nullish(),
        tintProductId: uuidSchema.nullish(),
        vlt: z.number().int().min(0).max(100),
        designMinutes: intNonNeg,
        fixedAddOnCents: moneyCents,
    }),
    z.object({
        serviceKind: z.literal('ceramic'),
        // §36: legacy tier path (packageKey) and the coating-product path
        // (coatingProductId) are mutually exclusive — exactly one is set. A migrated
        // product carries source_package_key so its minutes/cure-hold resolve from
        // the same ceramic_package_defs the tier path uses (exact equivalence).
        packageKey: z.enum(['silver', 'gold', 'platinum']).nullish(),
        coatingProductId: uuidSchema.nullish(),
        sizeClass: z.enum(SIZE_CLASSES),
        designMinutes: intNonNeg,
        fixedAddOnCents: moneyCents,
    }),
    z.object({
        serviceKind: z.literal('print'),
        designId: uuidSchema.nullable(),
        designName: z.string().max(200).nullable(),
        coverageLevel: z.enum(['full', 'three_quarter', 'half', 'spot']),
        spotFtX10: intNonNeg, // only used when coverageLevel === 'spot'
        spotMinutes: intNonNeg, // only used when coverageLevel === 'spot'
        laminateInventoryItemId: uuidSchema.nullable(),
        printCostCentsPerSqft: moneyCents, // outsourced print cost per sqft, 0 when printing in-house
        designMode: z.enum(['flat', 'hourly', 'waived']),
        designFlatCents: moneyCents,
        designMinutes: intNonNeg,
        revisionRounds: intNonNeg,
        removalTier: z.enum(['none', 'light', 'moderate', 'heavy_baked']),
        fixedAddOnCents: moneyCents,
    }),
]);
// ---------------------------------------------------------------------------
// §36 — coating products / tint products / addon services / service category
// minimums. Per-product shop catalogs (entity + price child table/array),
// mutable config like the PPF/Tint/Ceramic tabs above, not append-only.
// ---------------------------------------------------------------------------
// Coating products (§36) — per-product ceramic coating catalog, entity + per-size-bucket price child table.
export const coatingProductPriceMapSchema = z.object({
    coupe_sedan: intNonNeg.nullable(),
    suv_truck: intNonNeg.nullable(),
    van_oversize: intNonNeg.nullable(),
});
export const coatingProductSchema = z.object({
    id: uuidSchema,
    brand: z.string().nullable(),
    productName: z.string(),
    warrantyYears: intNonNeg.nullable(),
    layers: intNonNeg.nullable(),
    sourcePackageKey: z.enum(['silver', 'gold', 'platinum']).nullable(),
    installMinutes: intNonNeg.nullable(),
    cureHoldMinutes: intNonNeg.nullable(),
    inventoryItemId: uuidSchema.nullable(),
    active: z.boolean(),
    sort: z.number().int(),
    prices: coatingProductPriceMapSchema,
});
// Shop-created products (no source_package_key) MUST supply install/cure-hold minutes —
// mirrors the DB's coating_products_minutes_source CHECK constraint. Both fields are
// already required (non-optional) below, so the constraint is satisfied by construction;
// no additional .refine() is needed since a shop-created product per this schema always
// has both fields and no way to set sourcePackageKey.
export const coatingProductCreateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    productName: z.string().trim().min(1).max(120),
    warrantyYears: intNonNeg.nullish(),
    layers: intNonNeg.nullish(),
    installMinutes: intNonNeg,
    cureHoldMinutes: intNonNeg,
    inventoryItemId: uuidSchema.nullish(),
    sort: z.number().int().default(0),
    prices: z.object({
        coupe_sedan: intNonNeg.nullable(),
        suv_truck: intNonNeg.nullable(),
        van_oversize: intNonNeg.nullable(),
    }),
});
export const coatingProductUpdateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    productName: z.string().trim().min(1).max(120).optional(),
    warrantyYears: intNonNeg.nullish(),
    layers: intNonNeg.nullish(),
    installMinutes: intNonNeg.nullish(),
    cureHoldMinutes: intNonNeg.nullish(),
    inventoryItemId: uuidSchema.nullish(),
    active: z.boolean().optional(),
    sort: z.number().int().optional(),
    prices: z.object({
        coupe_sedan: intNonNeg.nullable().optional(),
        suv_truck: intNonNeg.nullable().optional(),
        van_oversize: intNonNeg.nullable().optional(),
    }).optional(),
});
// Tint products (§36)
export const tintProductPriceSchema = z.object({
    zoneKey: z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
    removalIncluded: z.boolean(),
});
export const tintProductSchema = z.object({
    id: uuidSchema,
    brand: z.string().nullable(),
    series: z.string(),
    vltOptions: z.array(z.string()),
    inventoryItemId: uuidSchema.nullable(),
    active: z.boolean(),
    sort: z.number().int(),
    prices: z.array(tintProductPriceSchema),
});
const tintProductPriceInputSchema = z.object({
    zoneKey: z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
    removalIncluded: z.boolean(),
});
export const tintProductCreateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    series: z.string().trim().min(1).max(120),
    vltOptions: z.array(z.string().trim().max(20)).default([]),
    inventoryItemId: uuidSchema.nullish(),
    sort: z.number().int().default(0),
    prices: z.array(tintProductPriceInputSchema).default([]),
});
export const tintProductUpdateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    series: z.string().trim().min(1).max(120).optional(),
    vltOptions: z.array(z.string().trim().max(20)).optional(),
    inventoryItemId: uuidSchema.nullish(),
    active: z.boolean().optional(),
    sort: z.number().int().optional(),
    prices: z.array(tintProductPriceInputSchema).optional(),
});
// PPF products (§36) — mirrors tint products above, but keyed by PPF zones
// (partial_front/full_front/track_pack/full_body/custom_panels) instead of
// tint zones, and productName instead of series (no vltOptions equivalent).
export const ppfProductPriceSchema = z.object({
    zoneKey: z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
});
export const ppfProductSchema = z.object({
    id: uuidSchema,
    brand: z.string().nullable(),
    productName: z.string(),
    inventoryItemId: uuidSchema.nullable(),
    active: z.boolean(),
    sort: z.number().int(),
    prices: z.array(ppfProductPriceSchema),
});
const ppfProductPriceInputSchema = z.object({
    zoneKey: z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
    sizeBucket: sizeBucketSchema,
    priceCents: intNonNeg,
});
export const ppfProductCreateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    productName: z.string().trim().min(1).max(120),
    inventoryItemId: uuidSchema.nullish(),
    sort: z.number().int().default(0),
    prices: z.array(ppfProductPriceInputSchema).default([]),
});
export const ppfProductUpdateSchema = z.object({
    brand: z.string().trim().max(80).nullish(),
    productName: z.string().trim().min(1).max(120).optional(),
    inventoryItemId: uuidSchema.nullish(),
    active: z.boolean().optional(),
    sort: z.number().int().optional(),
    prices: z.array(ppfProductPriceInputSchema).optional(),
});
// Addon services (§36)
export const addonServiceSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    pricingType: z.enum(['flat', 'hourly']),
    rateCents: intNonNeg,
    estimatedMinutes: intNonNeg.nullable(),
    active: z.boolean(),
    sort: z.number().int(),
});
export const addonServiceCreateSchema = z.object({
    name: z.string().trim().min(1).max(120),
    pricingType: z.enum(['flat', 'hourly']),
    rateCents: intNonNeg,
    estimatedMinutes: intNonNeg.nullish(),
    sort: z.number().int().default(0),
});
export const addonServiceUpdateSchema = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    pricingType: z.enum(['flat', 'hourly']).optional(),
    rateCents: intNonNeg.optional(),
    estimatedMinutes: intNonNeg.nullish(),
    sort: z.number().int().optional(),
});
// Service category minimums (§36)
export const serviceCategorySchema = z.enum(['wrap', 'ppf', 'tint', 'ceramic', 'print', 'default']);
export const serviceCategoryMinimumSchema = z.object({
    serviceCategory: serviceCategorySchema,
    minCents: intNonNeg,
    enabled: z.boolean(),
});
export const serviceCategoryMinimumPutSchema = z.object({
    minCents: intNonNeg,
    enabled: z.boolean(),
});
// ---------------------------------------------------------------------------
// Per-vehicle pricing (§36) — vehicle_panel_overrides / vehicle_*_product_prices
// (migration 0066). Every row is keyed to either a platform catalog vehicle
// or a workspace-custom vehicle, never both.
// ---------------------------------------------------------------------------
export const vehicleRefSourceSchema = z.enum(['catalog', 'workspace']);
/** One cell of the effective per-vehicle panel-pricing matrix - override
 * merged with workspace default merged with platform default, plus which
 * level resolved it. */
export const vehiclePanelPricingCellSchema = z.object({
    panelKey: z.enum(PANEL_KEYS),
    ftX10: intNonNeg,
    minutes: intNonNeg,
    overrideLevel: z.enum(['vehicle', 'workspace', 'default']),
});
/** PUT body for a single vehicle_panel_overrides row - a real upsert (rows
 * don't pre-exist), mirrors panelOverridePutSchema's shape. */
export const vehiclePanelOverridePutSchema = z.object({
    ftX10: intNonNeg,
    minutes: intNonNeg,
});
/** PUT body for a single vehicle_{coating,tint,ppf}_product_prices row -
 * zoneKey is required for tint/ppf products, omitted for ceramic. */
export const vehicleProductPricePutSchema = z.object({
    priceCents: intNonNeg,
    zoneKey: z
        .union([
        z.enum(['front_two', 'all_around', 'full_incl_windshield', 'windshield_strip', 'sunroof']),
        z.enum(['partial_front', 'full_front', 'track_pack', 'full_body', 'custom_panels']),
    ])
        .optional(),
});
export const vehicleServiceKindSchema = z.enum(['ceramic', 'tint', 'ppf']);
/** One product row in the per-vehicle service-products response - priceRows
 * is one row per zone for tint/ppf (zoneKey set) or a single row for ceramic
 * (zoneKey null). */
export const vehicleServiceProductRowSchema = z.object({
    productId: uuidSchema,
    brand: z.string().nullable(),
    productName: z.string(),
    inventoryItemId: uuidSchema.nullable(),
    priceRows: z.array(z.object({
        zoneKey: z.string().nullable(),
        basePriceCents: intNonNeg.nullable(),
        vehiclePriceCents: intNonNeg.nullable(),
    })),
});
/** PUT body for a single vehicle_service_quantities row. */
export const vehicleServiceQuantityPutSchema = z.object({
    quantity: z.number().nonnegative(),
});
/** GET response for a vehicle's material quantities across all three
 * service kinds - null for a kind with no quantity row set. */
export const vehicleServiceQuantitiesSchema = z.object({
    ceramic: z.number().nullable(),
    tint: z.number().nullable(),
    ppf: z.number().nullable(),
});
//# sourceMappingURL=pricing.js.map